import { relativeTime } from './relativeTime'
import type { FootballStatusResponse, SyncResourceStatus } from '../../../footballStatus'

/**
 * The matchday panel.
 *
 * `GET /api/dev/football-status` is the first thing anyone looks at on a matchday, and this is
 * where it is read. The rail renders in live mode, which is the mode it matters in.
 *
 * Every reading here is chosen for a question someone actually asks at 3pm on a Saturday, and
 * phrased as the answer rather than the raw field: relative times because the judgement is always
 * "is this stale", a bar for the budget because the question is "are we about to run out".
 */

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="gameweek-grid__dev-readout-row">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  )
}

function ResourceRow({
  resource,
  state,
  nowMs,
}: {
  resource: string
  state: SyncResourceStatus
  nowMs: number
}) {
  return (
    <>
      <Row label={resource} value={relativeTime(state.lastSuccessAt, nowMs)} />
      {state.lastError !== null && state.consecutiveFailures > 0 && (
        <div className="gameweek-grid__dev-warning">
          {resource}: {state.lastError} (×{state.consecutiveFailures})
        </div>
      )}
    </>
  )
}

export default function FootballStatusPanel({
  status,
  error,
  nowMs,
}: {
  status: FootballStatusResponse | null
  error: string | null
  /** When the payload arrived — see `DevPoll.receivedAtMs` for why this is not read from a clock
   *  during render. Null before the first poll lands, when there is nothing to date anyway. */
  nowMs: number | null
}) {
  if (error !== null) {
    return <div className="gameweek-grid__dev-warning">status unavailable: {error}</div>
  }
  if (!status) return <div className="gameweek-grid__dev-readout">loading status…</div>

  const budgetPercent =
    status.dailyBudget > 0
      ? Math.min(100, Math.round((status.requestsToday / status.dailyBudget) * 100))
      : 0

  return (
    <div className="gameweek-grid__dev-readout">
      {/* Which driver, pointed where — the single most useful line when something is
          misconfigured, on the boot log and equally at this rail. */}
      <Row label="driver" value={`${status.driver} · ${status.competition} ${status.season}`} />
      <Row label="base URL" value={status.baseUrl} />

      {!status.schedulerRunning && (
        <div className="gameweek-grid__dev-warning">
          scheduler not running — nothing is polling and nothing will self-heal
        </div>
      )}
      {!status.fixturesLoaded && (
        <div className="gameweek-grid__dev-warning">
          no fixtures loaded — the cold sync has not run and the season is empty
        </div>
      )}
      {status.paused === true && (
        <div className="gameweek-grid__dev-warning">
          paused{status.pausedUntil ? ` until ${status.pausedUntil}` : ''}
          {status.pausedReason ? `: ${status.pausedReason}` : ''}
        </div>
      )}

      {/* The live window and "is anything actually in play" disagreeing is itself the diagnosis. */}
      {status.liveWindowOpen !== null && (
        <Row label="live window" value={status.liveWindowOpen ? 'open' : 'closed'} />
      )}
      {status.liveWindowReason !== null && <Row label="why" value={status.liveWindowReason} />}
      {status.anyFixtureInPlay !== null && (
        <Row label="in play" value={status.anyFixtureInPlay ? 'yes' : 'no'} />
      )}

      {status.phase && (
        <Row
          label={`GW${status.phase.gameweek} phase`}
          value={`${status.phase.deadlinePassed ? 'locked' : 'open'} · ${
            status.phase.resultsRevealed ? 'results in' : 'awaiting results'
          }`}
        />
      )}

      <Row
        label="requests today"
        value={`${status.requestsToday}/${status.dailyBudget}${
          status.budgetLevel ? ` (${status.budgetLevel})` : ''
        }`}
      />
      <div className="gameweek-grid__dev-meter">
        <div
          className={`gameweek-grid__dev-meter-fill gameweek-grid__dev-meter-fill--${
            status.budgetLevel ?? 'normal'
          }`}
          style={{ width: `${budgetPercent}%` }}
        />
      </div>
      {status.quotaRemaining !== null && (
        <Row label="provider quota" value={String(status.quotaRemaining)} />
      )}

      {Object.entries(status.resources).map(([resource, state]) => (
        <ResourceRow key={resource} resource={resource} state={state} nowMs={nowMs ?? 0} />
      ))}
    </div>
  )
}
